import { isAlbum } from '../../../../../../util/album';
import {
  isAlbumFolderOwner,
  isAlbumOwner,
} from '../../../../../../util/account';
import { FEATURE_PERMISSION_TYPE } from '../../../../services/featurePermissions';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../constants/event-items';
import {
  getAlbumMembershipGroupSubscriptionAlbum,
  getAlbumMembershipGroupSubscriptionAlbumFolder,
} from '../../Matches/components/PurchasingOptions/helpers';
import { ALBUM_SUBTYPE } from '../../../../../../constants/albums';
import { ALL_PHOTOS_MODE } from '../../../../../../constants/album-folder';

/**
 * Determines if the route selector (All | Matches | Favorites) should render
 *
 * @param {Object} user the viewing user
 * @param {Object} hierarchyItem the viewed item (an album or albumFolder)
 * @param {Object} albumMembershipGroup the album membership group, if it exists
 * @param {Boolean} isAllowedToView whether or not the user has contributor permissions to view
 * @param {Boolean} isConsumerView whether or not the check is happening for consumer view (/consumer/matches)
 */
export const shouldRenderRouteSelector = (
  user,
  hierarchyItem,
  albumMembershipGroup,
  isAllowedToView = false,
  isConsumerView = false,
) => {
  const itemIsAlbum = isAlbum(hierarchyItem);
  const isOwner = itemIsAlbum
    ? isAlbumOwner(user, hierarchyItem)
    : isAlbumFolderOwner(user, hierarchyItem);

  // Case A
  // The item is an album with no photos
  // In this case, there is no need to show the route selector, as the user
  // will be limited by the choices present in the null state for the album.
  if (
    itemIsAlbum &&
    hierarchyItem?.photos?.total === 0 &&
    hierarchyItem?.videos?.total === 0
  ) {
    return false;
  }

  // Case B:
  // User owns the content, or is allowed to view it.
  // In both cases, all photos / matches & favorites should be available.
  if (isOwner || isAllowedToView) {
    return true;
  }

  if (!isConsumerView && hierarchyItem?.account_favorite_photos?.total > 0) {
    return true;
  }

  // Case C:
  // User is joined to the content OR there are videos and no sub.
  // In this case, wether or not the user has access to the content
  // depends on the value of album_memberships_has_all_photos_access
  if (albumMembershipGroup) {
    const subscriptionAlbumFolder =
      getAlbumMembershipGroupSubscriptionAlbumFolder(albumMembershipGroup);
    const subscriptionAlbum =
      getAlbumMembershipGroupSubscriptionAlbum(albumMembershipGroup);

    const subscriptionAvailable =
      !!albumMembershipGroup &&
      (!!subscriptionAlbumFolder || !!subscriptionAlbum);

    const hasPurchasedIdentity = !!albumMembershipGroup?.identities?.find(
      i => i.has_purchased_subscription,
    );

    return (
      albumMembershipGroup.album_memberships_has_all_photos_access ||
      (!isConsumerView &&
        hierarchyItem?.videos?.total > 0 &&
        (!subscriptionAvailable || hasPurchasedIdentity))
    );
  }

  // Case D:
  // User is NOT Joined nor owns an album item.
  // In this case, Backend will return has_all_photos_access as true or not
  // based off any reasoning that would affect availability (i.e PD all Photos)
  if (itemIsAlbum) {
    return hierarchyItem?.has_all_photos_access;
  }

  // Case E:
  // User is not Joined, nor owns an album folder item
  // In this case, we do not know if the user has access to everything, so assume
  // they do. This will have the effect of allowing users to traverse the hierarchy
  // but if a given album is blocked they will still be redirected away
  return true;
};

/**
 * Determine if the Photos header should be rendered
 *
 * @param {Object} user the viewing user
 * @param {Object} hierarchyItem the viewed item (an album or albumFolder)
 * @param {Object} albumMembershipGroup the album membership group, if it exists
 * @param {Boolean} isAllowedToView whether or not the user has contributor permissions to view
 */
export const shouldRenderPhotos = (
  user,
  hierarchyItem,
  albumMembershipGroup,
  isAllowedToView = false,
) => {
  const itemIsAlbum = isAlbum(hierarchyItem);
  const isOwner = itemIsAlbum
    ? isAlbumOwner(user, hierarchyItem)
    : isAlbumFolderOwner(user, hierarchyItem);

  // Case A
  // The item is an album with no photos
  // In this case, there is no need to show the route selector, as the user
  // will be limited by the choices present in the null state for the album.
  if (
    itemIsAlbum &&
    hierarchyItem?.photos?.total === 0 &&
    hierarchyItem?.videos?.total === 0
  ) {
    return false;
  }

  // Case B:
  // User owns the content, or is allowed to view it.
  // In both cases, all photos / matches & favorites should be available.
  if (isOwner || isAllowedToView) {
    return true;
  }

  // Case C:
  // User is joined to the content.
  // In this case, wether or not the user has access to the content
  // depends on the value of album_memberships_has_all_photos_access
  if (albumMembershipGroup) {
    return albumMembershipGroup.album_memberships_has_all_photos_access;
  }

  // Case D:
  // User is NOT Joined nor owns an album item.
  // In this case, Backend will return has_all_photos_access as true or not
  // based off any reasoning that would affect availability (i.e PD all Photos)
  if (itemIsAlbum) {
    return hierarchyItem?.has_all_photos_access;
  }

  // Case E:
  // User is not Joined, nor owns a picture day album folder item
  // In this case we need to check if the item allows for all_photos_mode
  // if it does we can allow the user to traverse. Otherwise we need to move
  // block traversal
  if (
    hierarchyItem?.picture_day_enabled &&
    hierarchyItem?.all_photos_mode !== ALL_PHOTOS_MODE.PUBLIC
  ) {
    return false;
  }

  // Case F:
  // User is not Joined, nor owns an album folder item
  // In this case, we do not know if the user has access to everything, so assume
  // they do. This will have the effect of allowing users to traverse the hierarchy
  // but if a given album is blocked they will still be redirected away
  return true;
};

/**
 * Determine if the Matches header should be rendered
 *
 * @param {Object} opts the list of options/items
 * @param {Object} opts.hierarchyItem the album or album folder
 * @param {Object} opts.albumMembershipGroup the album membership group
 * @param {boolean} opts.isContributor whether the current user is a contributor or not
 * @param {Object} opts.featurePermissionsItem the album membership group
 * @returns {boolean}
 */
export const shouldRenderMatches = ({
  hierarchyItem,
  albumMembershipGroup,
  isContributor,
  featurePermissionsItem,
}) => {
  if (hierarchyItem?.is_video_album) {
    return false;
  }

  if (
    hierarchyItem?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    ) ||
    hierarchyItem?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    )
  ) {
    return false;
  }

  const isMatchingDisabled =
    hierarchyItem?.matching_disabled ||
    hierarchyItem?.root_album_folder?.matching_disabled;

  // If matching is disabled and there are no matched photos there
  // can't be any matches, so disable.
  if (
    isMatchingDisabled &&
    (!albumMembershipGroup ||
      albumMembershipGroup?.matched_photos_extended?.total === 0)
  ) {
    return false;
  }

  // For purchase event addons, this should always be,
  // as it is always a purchasable feature
  if (
    featurePermissionsItem.type ===
      FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES &&
    !isContributor
  ) {
    return true;
  }

  // For purchased events, even if joins disabled,
  // this is a purchasable feature.
  if (
    featurePermissionsItem.type ===
      FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_FEATURES &&
    (featurePermissionsItem.joins_enabled?.plans ?? []).length > 0 &&
    !isContributor
  ) {
    return true;
  }

  if (!hierarchyItem?.joins_enabled && !albumMembershipGroup) {
    return false;
  }

  return true;
};

/**
 * Determine if the Favorites header should be rendered
 *
 * @param {Object} hierarchyItem the album or album folder
 * @returns {boolean}
 */
export const shouldRenderFavorites = hierarchyItem => {
  if (!hierarchyItem) {
    return false;
  }

  if (hierarchyItem?.is_video_album) {
    return false;
  }

  if (hierarchyItem?.account_favorite_photos?.total === 0) {
    return false;
  }

  return true;
};

/**
 * Determine if the Blogs header should be rendered
 *
 * @param {Object} options the options
 * @param {Object} options.hierarchyItem the album or album folder
 * @param {Object} options.albumMembershipGroup the album membership group
 * @param {number} options.blogPostsTotal the total number of blog posts
 * @returns {boolean}
 */
export const shouldRenderBlogs = ({
  hierarchyItem,
  albumMembershipGroup,
  blogPostsTotal = 0,
}) => {
  const itemIsAlbum = isAlbum(hierarchyItem);

  if (hierarchyItem?.album_subtype === ALBUM_SUBTYPE.CAMPS && !itemIsAlbum) {
    return false;
  }
  if (blogPostsTotal === 0) {
    return false;
  }

  if (albumMembershipGroup) {
    const subscriptionAlbumFolder =
      getAlbumMembershipGroupSubscriptionAlbumFolder(albumMembershipGroup);
    const subscriptionAlbum =
      getAlbumMembershipGroupSubscriptionAlbum(albumMembershipGroup);

    const subscriptionAvailable =
      !!albumMembershipGroup &&
      (!!subscriptionAlbumFolder || !!subscriptionAlbum);

    const hasPurchasedIdentity = !!albumMembershipGroup?.identities?.find(
      i => i.has_purchased_subscription,
    );

    return (
      albumMembershipGroup.album_memberships_has_all_photos_access ||
      (blogPostsTotal > 0 && (!subscriptionAvailable || hasPurchasedIdentity))
    );
  }

  return true;
};

/**
 * Determine if the Favorites header should be rendered
 *
 * @param {Object} options the options
 * @param {Object} options.hierarchyItem the album or album folder
 * @param {Object} options.albumMembershipGroup the album membership group
 * @returns {boolean}
 */
export const shouldRenderVideos = ({ hierarchyItem, albumMembershipGroup }) => {
  const itemIsAlbum = isAlbum(hierarchyItem);

  if (hierarchyItem?.album_subtype === ALBUM_SUBTYPE.CAMPS && !itemIsAlbum) {
    return false;
  }
  if (hierarchyItem?.is_video_album || !hierarchyItem?.videos?.total) {
    return false;
  }

  if (albumMembershipGroup) {
    const subscriptionAlbumFolder =
      getAlbumMembershipGroupSubscriptionAlbumFolder(albumMembershipGroup);
    const subscriptionAlbum =
      getAlbumMembershipGroupSubscriptionAlbum(albumMembershipGroup);

    const subscriptionAvailable =
      !!albumMembershipGroup &&
      (!!subscriptionAlbumFolder || !!subscriptionAlbum);

    const hasPurchasedIdentity = !!albumMembershipGroup?.identities?.find(
      i => i.has_purchased_subscription,
    );

    return (
      albumMembershipGroup.album_memberships_has_all_photos_access ||
      (hierarchyItem?.videos?.total > 0 &&
        (!subscriptionAvailable || hasPurchasedIdentity))
    );
  }

  return true;
};
